// The asteroid belt as it is: 165,000 numbered asteroids from JPL's elements
// (public/data/asteroids.bin.gz, scripts/build-asteroids.mjs; credit
// NASA/JPL-Caltech), the brightest 150,000 of the main belt with every Hilda
// and Jupiter Trojan, each on its own orbit. The vertex shader solves
// Kepler's equation for the sim clock, so the Kirkwood gaps, the Hildas'
// triangle and the two Trojan swarms 60 degrees either side of Jupiter are
// where the data puts them, and the inner belt laps the outer.
//
// Each is a point of light at its apparent magnitude from wherever the camera
// is (H, G system: Bowell et al. 1989, Asteroids II, 524-556), drawn with
// the star layers' point-spread function and exposure (src/stars.js), so at
// true scale they are what they are: Vesta and Ceres near the naked-eye
// limit from the Earth, the rest far below it. Lit (the Asteroids switch,
// off by default) draws them by size instead, bright enough to see the
// belt's shape from anywhere.
//
// Osculating elements at one epoch, without planetary perturbations: right
// to a fraction of a degree within a few years of it (the table's epoch is
// in 2026), drifting further out from there.
import * as THREE from 'three';
import { STAR_REACH_GLSL, STAR_FRAGMENT, gunzipIfNeeded, unitLuminanceColour } from '../stars.js';

const URL = '/data/asteroids.bin.gz';
// A little redder than sunlight (the Sun's B-V is 0.65), as most asteroids'
// reflectance rises toward the red.
const ASTEROID_BV = 0.75;

const VERTEX = /* glsl */`
  // position: a, e, i (normalised); elB: node, argument of perihelion,
  // mean anomaly at the epoch; elH: H / 25.5, G / 2.55.
  attribute vec3 elB;
  attribute vec2 elH;
  uniform vec2 uARange;
  uniform float uDays;
  uniform float uArtistic;
  uniform float uUnitsPerAu;
  uniform float uSpread;
  uniform vec4 uMapAu;
  uniform vec4 uMapScene;
  uniform vec3 uCamAu;
  uniform mat3 uViewRot;
  uniform float uExposure;
  uniform float uLit;
  uniform float uLitOffset;
  uniform float pixelRatio;
  uniform vec3 uColor;
  varying vec3 vColor;
  varying float vPeak;
  varying float vHalfSize;
  ${STAR_REACH_GLSL}
  const float TWO_PI = 6.28318530718;
  // Artistic distance for a semi-major axis: piecewise linear through
  // Mars, the belt's edges and Jupiter as the artistic layout spaces them.
  float artisticDistance(float a) {
    if (a < uMapAu.y) return mix(uMapScene.x, uMapScene.y, (a - uMapAu.x) / (uMapAu.y - uMapAu.x));
    if (a < uMapAu.z) return mix(uMapScene.y, uMapScene.z, (a - uMapAu.y) / (uMapAu.z - uMapAu.y));
    return mix(uMapScene.z, uMapScene.w, (a - uMapAu.z) / (uMapAu.w - uMapAu.z));
  }
  void main() {
    float a = mix(uARange.x, uARange.y, position.x);
    float e = position.y;
    float inc = position.z * TWO_PI;
    float node = elB.x * TWO_PI, peri = elB.y * TWO_PI;
    float M = mod(elB.z * TWO_PI + 0.01720209895 * pow(a, -1.5) * uDays, TWO_PI);
    float E = M + e * sin(M);
    for (int k = 0; k < 5; k++) E -= (E - e * sin(E) - M) / (1.0 - e * cos(E));
    float xo = a * (cos(E) - e), yo = a * sqrt(1.0 - e * e) * sin(E);
    float cO = cos(node), sO = sin(node), cw = cos(peri), sw = sin(peri), ci = cos(inc), si = sin(inc);
    vec3 ecl = vec3(
      (cO * cw - sO * sw * ci) * xo + (-cO * sw - sO * cw * ci) * yo,
      (sO * cw + cO * sw * ci) * xo + (-sO * sw + cO * cw * ci) * yo,
      sw * si * xo + cw * si * yo);
    // J2000 ecliptic to the scene: (x, y, z) -> (x, z, -y), in AU.
    vec3 au = vec3(ecl.x, ecl.z, -ecl.y);
    float scale = uArtistic > 0.5 ? artisticDistance(a) * uSpread / a : uUnitsPerAu;
    // Relative to the camera before anything is scaled up: at true scale a
    // float32 scene position near the belt is good to only 30,000 km, the
    // difference in AU to a few tens.
    vec3 rel = uArtistic > 0.5 ? au * scale - cameraPosition : (au - uCamAu) * uUnitsPerAu;
    float dScene = max(length(rel), 1e-9);
    float r = length(au);
    float delta = dScene / scale;
    float cosPhase = clamp(dot(au, rel) / (r * dScene), -1.0, 1.0);
    float t = tan(0.5 * acos(cosPhase));
    float H = elH.x * 25.5, G = elH.y * 2.55;
    float phi = (1.0 - G) * exp(-3.33 * pow(t, 0.63)) + G * exp(-1.87 * pow(t, 1.22));
    float phaseMag = -2.5 * log(max(phi, 1e-6)) * 0.4342945;
    // True: the apparent magnitude. Lit: a display brightness from the
    // asteroid's size (H) that falls only gently with distance, so the belt
    // reads from across the system and stays a field of points up close.
    float mag = uLit > 0.5
      ? H - uLitOffset + 1.25 * log(max(delta, 0.05)) * 0.4342945 + phaseMag
      : H + 5.0 * log(r * delta) * 0.4342945 + phaseMag;
    float peak = pow(10.0, -0.4 * (mag - 6.0)) * uExposure;
    vColor = uColor;
    vPeak = peak;
    float rr = starReach(peak);
    gl_Position = projectionMatrix * vec4(uViewRot * rel, 1.0);
    if (rr <= 0.0) {
      gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
      gl_PointSize = 0.0;
      vHalfSize = 0.0;
      return;
    }
    float hs = clamp(rr, 1.5, 40.0) * pixelRatio;
    vHalfSize = hs;
    gl_PointSize = 2.0 * hs + 1.0;
  }
`;

// named: [{ a, e, i, node, peri, M (degrees), H, G }] at the file's epoch,
// drawn exactly (their models stand at the same places in main.js).
export async function createAsteroids(renderer, { maxCount = Infinity, named = [] } = {}) {
  const res = await fetch(URL);
  if (!res.ok) throw new Error(`Failed to load ${URL}`);
  const buf = await gunzipIfNeeded(await res.arrayBuffer());
  const view = new DataView(buf);
  if (String.fromCharCode(view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3)) !== 'AST1') throw new Error('asteroids: bad file');
  const total = view.getUint32(4, true);
  const epochJd = view.getFloat64(8, true);
  const aMin = view.getFloat32(16, true), aMax = view.getFloat32(20, true);
  const n = Math.min(total, maxCount);
  const field16 = (k) => new Uint16Array(buf, 24 + k * total * 2, total).subarray(0, n);
  const bytes = (k) => new Uint8Array(buf, 24 + 6 * total * 2 + k * total, total).subarray(0, n);
  const posA = new Uint16Array(n * 3), elB = new Uint16Array(n * 3), elH = new Uint8Array(n * 2);
  const [fa, fe, fi, fnode, fperi, fm] = [0, 1, 2, 3, 4, 5].map(field16);
  const fh = bytes(0), fg = bytes(1);
  for (let k = 0; k < n; k++) {
    posA[k * 3] = fa[k]; posA[k * 3 + 1] = fe[k]; posA[k * 3 + 2] = fi[k];
    elB[k * 3] = fnode[k]; elB[k * 3 + 1] = fperi[k]; elB[k * 3 + 2] = fm[k];
    elH[k * 2] = fh[k]; elH[k * 2 + 1] = fg[k];
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(posA, 3, true));
  geometry.setAttribute('elB', new THREE.BufferAttribute(elB, 3, true));
  geometry.setAttribute('elH', new THREE.BufferAttribute(elH, 2, true));
  // The attributes are elements, not places: no bounds to compute.
  geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), Infinity);

  const colour = [0, 0, 0];
  unitLuminanceColour(ASTEROID_BV, colour, 0);
  const material = new THREE.ShaderMaterial({
    uniforms: {
      uARange: { value: new THREE.Vector2(aMin, aMax) },
      uDays: { value: 0 },
      uArtistic: { value: 0 },
      uUnitsPerAu: { value: 1 },
      uSpread: { value: 1 },
      uMapAu: { value: new THREE.Vector4(1.524, 2.1, 3.3, 5.203) },
      uMapScene: { value: new THREE.Vector4(200, 240, 290, 320) },
      uCamAu: { value: new THREE.Vector3() },
      uViewRot: { value: new THREE.Matrix3() },
      uExposure: { value: 0 },
      uLit: { value: 0 },
      uLitOffset: { value: 0 },
      uColor: { value: new THREE.Vector3(...colour) },
      pixelRatio: { value: renderer ? renderer.getPixelRatio() : 1 },
      opacity: { value: 1 },
    },
    vertexShader: VERTEX,
    fragmentShader: STAR_FRAGMENT,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: THREE.AdditiveBlending,
  });
  // Shrunk with the stars when the sky is drawn into a cube (wormhole lens).
  material.userData.starPoints = true;
  const pointsOf = (geo, name) => {
    const p = new THREE.Points(geo, material);
    p.name = name;
    p.renderOrder = -997;
    p.frustumCulled = false;
    p.raycast = () => {};
    return p;
  };
  // The same attributes in float for the named few, in the units the
  // normalised ones decode to.
  const exact = new THREE.BufferGeometry();
  const deg = (v) => v / 360;
  exact.setAttribute('position', new THREE.BufferAttribute(new Float32Array(named.flatMap((b) => [(b.a - aMin) / (aMax - aMin), b.e, deg(b.i)])), 3));
  exact.setAttribute('elB', new THREE.BufferAttribute(new Float32Array(named.flatMap((b) => [deg(b.node), deg(b.peri), deg(((b.M % 360) + 360) % 360)])), 3));
  exact.setAttribute('elH', new THREE.BufferAttribute(new Float32Array(named.flatMap((b) => [b.H / 25.5, (b.G ?? 0.15) / 2.55])), 2));
  exact.boundingSphere = new THREE.Sphere(new THREE.Vector3(), Infinity);
  // Named as the belt it replaces, so the layers that hide the belt (a held
  // famous photo, the lighter VR tiers, rocketry) still find it.
  const group = new THREE.Group();
  group.name = 'asteroidBelt';
  group.add(pointsOf(geometry, 'asteroid-points'), pointsOf(exact, 'asteroid-named-points'));
  const _rot = new THREE.Matrix3();

  // active: Sol is the system on screen; jd: the sim clock; artistic: the
  // artistic layout (mapScene its anchors in base units, spread the scale
  // slider); unitsPerAu: true scale's.
  // lit: the display brightness, litOffset magnitudes brighter than H.
  function update({ active, camera, jd, artistic, unitsPerAu, spread, mapScene, exposure, lit, litOffset }) {
    geometry.setDrawRange(0, active ? n : 0);
    exact.setDrawRange(0, active ? named.length : 0);
    if (!active) return;
    const u = material.uniforms;
    camera.updateMatrixWorld();
    u.uViewRot.value.copy(_rot.setFromMatrix4(camera.matrixWorldInverse));
    u.uCamAu.value.setFromMatrixPosition(camera.matrixWorld).divideScalar(unitsPerAu);
    u.uDays.value = jd - epochJd;
    u.uArtistic.value = artistic ? 1 : 0;
    u.uUnitsPerAu.value = unitsPerAu;
    u.uSpread.value = spread;
    if (mapScene) u.uMapScene.value.set(...mapScene);
    u.uExposure.value = exposure;
    u.uLit.value = lit ? 1 : 0;
    u.uLitOffset.value = litOffset;
    if (renderer) u.pixelRatio.value = renderer.getPixelRatio();
  }

  return { mesh: group, count: n, epochJd, update, dispose: () => { geometry.dispose(); exact.dispose(); material.dispose(); } };
}
